import React from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

const config = require('../../config.json');

/* ────────────────────────────────────────────────────────────────────────────
   다운로드 경로 (네이버 클라우드 오브젝트 스토리지)

   파일명 규칙이 정해져 있으므로 조합해서 만든다. 버전을 올리면 APP_VERSION 한 줄만
   고치면 여섯 개 주소가 모두 따라온다.

     mac : <base>/mac/JetaTrack-Music-Player-<env>-<버전>-universal.dmg
     win : <base>/window/JetaTrack-Music-Player-<env>-<버전>-x64.exe

   ★ APP_VERSION 은 web/musicplayer/package.json 의 version 과 반드시 같아야 한다.
     빌드 산출물 이름이 그 값으로 만들어지기 때문이다. 어긋나면 404 가 난다.

   ★ 파일명의 환경 조각은 dev / test / live 다. config.json 의 service 값은
     'testserver' 이므로 아래 매핑에서 'test' 로 바꿔 쓴다.
   ──────────────────────────────────────────────────────────────────────────── */
const DOWNLOAD_BASE = 'https://kr.object.ncloudstorage.com/qnoss/musicapp';
const APP_VERSION = '1.0.0';

const macUrl = (env) => `${DOWNLOAD_BASE}/mac/JetaTrack-Music-Player-${env}-${APP_VERSION}-universal.dmg`;
const winUrl = (env) => `${DOWNLOAD_BASE}/window/JetaTrack-Music-Player-${env}-${APP_VERSION}-x64.exe`;

const DOWNLOADS = {
  dev:        { mac: macUrl('dev'),  win: winUrl('dev') },
  testserver: { mac: macUrl('test'), win: winUrl('test') },
  live:       { mac: macUrl('live'), win: winUrl('live') },
};

//--> config 의 service 를 위 키로 정규화한다. 모르는 값이면 개발로 떨어뜨린다.
const resolveService = () => {
  const s = (config && config.service) || 'dev';
  if (s === 'test') return 'testserver';
  return DOWNLOADS[s] ? s : 'dev';
};

const detectPlatform = () => {
  try {
    const ua = (navigator.userAgent || '') + ' ' + (navigator.platform || '');
    if (/Mac|iPhone|iPad/i.test(ua)) return 'mac';
    if (/Win/i.test(ua)) return 'win';
  } catch (e) {}
  return '';
};

/* 로고는 외부 이미지를 걸지 않고 인라인 SVG 로 넣는다. 매장 PC 가 외부망을 못 타거나
   원본 주소가 죽어도 깨지지 않아야 한다.
     Apple   — simple-icons (viewBox 24)
     Windows — Bootstrap Icons (viewBox 16) */
const AppleMark = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width={props.size} height={props.size} aria-hidden="true">
    <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
  </svg>
);

const WindowsMark = (props) => (
  <svg viewBox="0 0 16 16" fill="currentColor" width={props.size} height={props.size} aria-hidden="true">
    <path d="M6.555 1.375 0 2.237v5.45h6.555zM0 13.795l6.555.933V8.313H0zm7.278-5.4.026 6.378L16 16V8.395zM16 0 7.33 1.244v6.414H16z" />
  </svg>
);

const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #0d0d0d 0%, #1a1a2e 100%);
  color: #fff;
  display: flex;
  justify-content: center;
`;

const Content = styled.div`
  width: 100%;
  max-width: 680px;
  padding: 2.75rem 1.75rem 2rem;
  box-sizing: border-box;
`;

const Kicker = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #e74c8a;
  margin-bottom: 0.6rem;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 800;
  line-height: 1.35;
  margin: 0 0 1.5rem;
`;

/* 이 페이지의 핵심 안내. 지점에서 이걸 못 보고 웹으로만 틀다가 창을 내려
   음악이 끊기는 사고가 반복돼서, 눈에 띄게 키워 맨 위에 둔다. */
const NoticeBox = styled.div`
  padding: 1.15rem 1.25rem;
  background: rgba(231, 76, 138, 0.12);
  border: 1px solid rgba(231, 76, 138, 0.45);
  border-radius: 12px;
  margin-bottom: 2rem;
`;

const NoticeLine = styled.div`
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.6;
  color: #fff;
  display: flex;
  gap: 0.5rem;

  & + & {
    margin-top: 0.6rem;
  }
`;

const NoticeMark = styled.span`
  color: #e74c8a;
  flex-shrink: 0;
`;

const NoticeStrong = styled.span`
  color: #ffb3d1;
`;

const WebButton = styled.button`
  width: 100%;
  padding: 0.95rem 1rem;
  font-size: 0.95rem;
  font-weight: 700;
  font-family: inherit;
  color: rgba(255, 255, 255, 0.85);
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 0.9rem;
  margin: 1.75rem 0 1.25rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.45);
  white-space: nowrap;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: rgba(255, 255, 255, 0.12);
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 1rem;

  @media (max-width: 560px) {
    flex-direction: column;
  }
`;

const DownloadButton = styled.a`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.6rem;
  padding: 1.6rem 1rem;
  border-radius: 14px;
  text-decoration: none;
  text-align: center;
  border: 1px solid ${props => props.recommended ? 'rgba(231, 76, 138, 0.65)' : 'rgba(255, 255, 255, 0.16)'};
  background: ${props => props.recommended ? 'rgba(231, 76, 138, 0.18)' : 'rgba(255, 255, 255, 0.06)'};
  color: #fff;
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  opacity: ${props => props.disabled ? 0.4 : 1};
  pointer-events: ${props => props.disabled ? 'none' : 'auto'};
  transition: background 0.15s, transform 0.15s;

  &:hover {
    background: ${props => props.recommended ? 'rgba(231, 76, 138, 0.28)' : 'rgba(255, 255, 255, 0.12)'};
    transform: translateY(-2px);
  }
`;

const MarkWrap = styled.div`
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
`;

const ButtonLabel = styled.div`
  font-size: 1rem;
  font-weight: 700;
`;

const ButtonSub = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
`;

const RunningTrackMusicDownloadPage = () => {
  const location = useLocation();
  const service = resolveService();
  const preset = DOWNLOADS[service];
  const platform = detectPlatform();

  //--> 이 페이지는 '음악 재생' 을 누르면 먼저 뜨는 관문이다. 클럽 정보를 그대로 물고 있다가
  //    '웹페이지에서 보기' 를 누르면 같은 창에서 재생 화면으로 넘긴다.
  const params = new URLSearchParams(location.search);
  const clubId = params.get('clubId');
  const clubName = params.get('clubName');

  React.useEffect(() => {
    document.title = '제타트랙 뮤직 플레이어';
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    const prevHtmlBg = html.style.backgroundColor;
    const prevBodyBg = body.style.backgroundColor;
    const prevRootBg = root ? root.style.backgroundColor : '';
    const prevRootPadding = root ? root.style.paddingBottom : '';

    html.style.backgroundColor = '#0d0d0d';
    body.style.backgroundColor = '#0d0d0d';
    if (root) {
      root.style.backgroundColor = '#0d0d0d';
      // index.css 의 전역 `#root { padding-bottom: 8rem }` 이 이 페이지에선 빈 여백으로만
      // 남아 스크롤까지 만든다. 전역 규칙을 건드리지 않고 이 페이지에서만 걷어낸다.
      root.style.paddingBottom = '0';
    }

    return () => {
      html.style.backgroundColor = prevHtmlBg;
      body.style.backgroundColor = prevBodyBg;
      if (root) {
        root.style.backgroundColor = prevRootBg;
        root.style.paddingBottom = prevRootPadding;
      }
    };
  }, []);

  const handleOpenWeb = () => {
    let target = '/runningtrack/music';
    if (clubId) {
      target += `?clubId=${encodeURIComponent(clubId)}&clubName=${encodeURIComponent(clubName || '')}`;
    }
    window.location.href = target;
  };

  const renderDownload = (key, mark, label, sub) => {
    const href = preset[key];
    const ready = !!href;
    return (
      // download 속성은 교차 출처 주소에서는 브라우저가 무시한다. 실제 저장은 스토리지가
      // 내려주는 Content-Type(dmg=x-apple-diskimage, exe=x-msdownload)이 처리한다.
      <DownloadButton
        href={ready ? href : undefined}
        disabled={!ready}
        recommended={platform === key}
        download
      >
        <MarkWrap>{mark}</MarkWrap>
        <ButtonLabel>{label}</ButtonLabel>
        <ButtonSub>{ready ? sub : '준비 중'}</ButtonSub>
      </DownloadButton>
    );
  };

  return (
    <PageWrapper>
      <Content>
        <Kicker>JetaTrack Music Player</Kicker>
        <Title>제타트랙 앱에 뮤직비디오를<br />재생하는 플레이어 입니다.</Title>

        <NoticeBox>
          <NoticeLine>
            <NoticeMark>！</NoticeMark>
            <span>웹페이지로 재생할 경우 <NoticeStrong>페이지를 항상 열어놔야 합니다.</NoticeStrong></span>
          </NoticeLine>
          <NoticeLine>
            <NoticeMark>！</NoticeMark>
            <span>다른 작업을 하면서 음악을 재생하시려면 <NoticeStrong>프로그램을 설치해 주세요.</NoticeStrong></span>
          </NoticeLine>
        </NoticeBox>

        <WebButton onClick={handleOpenWeb}>웹페이지에서 보기</WebButton>

        <Divider>다른 작업을 하면서 들으려면</Divider>

        <ButtonRow>
          {renderDownload('mac', <AppleMark size={34} />, '맥 프로그램 다운받기', 'Intel · Apple Silicon 공용 (.dmg)')}
          {renderDownload('win', <WindowsMark size={30} />, '윈도우 프로그램 다운받기', '64비트 설치 파일 (.exe)')}
        </ButtonRow>
      </Content>
    </PageWrapper>
  );
};

export default RunningTrackMusicDownloadPage;
